import { useMemo, useState, type JSX } from 'react'
import { View, ViewHeader } from '@/components'
import { TabsContent } from '@ttab/elephant-ui'
import { TableProvider } from '@/contexts/TableProvider'
import { TableCommandMenu } from '@/components/Commands/TableCommand'
import { Header } from '@/components/Header'
import { AssignmentsList } from './AssignmentsList'
import { assignmentColumns } from './AssignmentColumns'
import { Commands } from '@/components/Commands'
import { useAuthors } from '@/hooks/useAuthors'
import { useRegistry } from '@/hooks/useRegistry'
import { useSession } from 'next-auth/react'
import { type ViewMetadata } from '@/types'
import { getAuthorBySub } from '@/lib/getAuthorBySub'
import { useQuery } from '@/hooks/useQuery'
import { newLocalDate } from '@/shared/datetime'
import { useSections } from '@/hooks/useSections'
import type { Assignment } from '@/shared/schemas/assignments'
import { useInitFilters } from '@/hooks/useInitFilters'
import { useTranslation } from 'react-i18next'

const meta: ViewMetadata = {
  name: 'Assignments',
  path: `${import.meta.env.BASE_URL}/assignments`,
  widths: {
    sm: 12,
    md: 12,
    lg: 6,
    xl: 6,
    '2xl': 6,
    hd: 6,
    fhd: 4,
    qhd: 3,
    uhd: 2
  }
}

export const Assignments = (): JSX.Element => {
  const [query] = useQuery()
  const [currentTab, setCurrentTab] = useState<string>('list')
  const authors = useAuthors()
  const { locale, timeZone } = useRegistry()
  const { data: session } = useSession()
  const sections = useSections()
  const { t } = useTranslation()

  const assigneeId = useMemo(
    () => getAuthorBySub(authors, session?.user?.sub)?.id,
    [authors, session?.user?.sub]
  )

  const date = useMemo(() => {
    return (typeof query.from === 'string')
      ? newLocalDate(timeZone, { date: query.from })
      : newLocalDate(timeZone)
  }, [query.from, timeZone])


  const columns = useMemo(() =>
    assignmentColumns({ authors, locale, timeZone, sections, currentDate: date, t }), [authors, locale, timeZone, sections, date, t])

  const columnFilters = useInitFilters({
    path: 'filters.Assignments.current',
    columns
  })

  return (
    <View.Root tab={currentTab} onTabChange={setCurrentTab}>
      <TableProvider<Assignment>
        type={meta.name}
        columns={columns}
        initialState={{
          grouping: ['startTime'],
          columnFilters,
          sorting: [
            { id: 'startTime', desc: false },
            { id: 'assignment_time', desc: false }
          ],
          globalFilter: query.query
        }}
      >
        <TableCommandMenu heading='Assignments'>
          <Commands />
        </TableCommandMenu>

        <ViewHeader.Root>
          <ViewHeader.Content>
            <ViewHeader.Title name={meta.name} title={t('views:assignments.title')} />
            <Header type={meta.name} assigneeId={assigneeId} />
          </ViewHeader.Content>

          <ViewHeader.Action />
        </ViewHeader.Root>

        <View.Content>
          <TabsContent value='list' className='mt-0'>
            <AssignmentsList date={date} columns={columns} />
          </TabsContent>
        </View.Content>

      </TableProvider>
    </View.Root>
  )
}

Assignments.meta = meta
